import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useHistory } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import {
  GET_MENU_PROFITABILITY,
  GET_FOOD_COST_REPORT,
  GET_BRANCHES,
} from "./queries";
import { MenuProfitabilityItem } from "./models";
import InventoryNav from "./inventoryNav";

const FoodCostList: React.FC = () => {
  const history = useHistory();

  // Date range defaults to current month
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const today = now.toISOString().split("T")[0];

  const [startDate, setStartDate] = useState(firstDay);
  const [endDate, setEndDate] = useState(today);
  const [branchFilter, setBranchFilter] = useState("");

  const [fetchProfitability, { data: profData, loading: loadingProf }] =
    useLazyQuery(GET_MENU_PROFITABILITY, { fetchPolicy: "no-cache" });
  const [fetchFoodCost, { data: fcData, loading: loadingFC }] =
    useLazyQuery(GET_FOOD_COST_REPORT, { fetchPolicy: "no-cache" });
  const [fetchBranches, { data: branchData }] = useLazyQuery(GET_BRANCHES, {
    fetchPolicy: "no-cache",
  });

  useEffect(() => {
    fetchBranches();
    fetchProfitability();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    fetchFoodCost({
      variables: {
        startDate,
        endDate,
        branch_id: branchFilter ? parseInt(branchFilter) : undefined,
      },
    });
    //eslint-disable-next-line
  }, [startDate, endDate, branchFilter]);

  const report = profData?.getMenuProfitability;
  const foodCost = fcData?.getFoodCostReport;
  const items: MenuProfitabilityItem[] = report?.items || [];

  const getMarginClass = (marginPct: number | null): string => {
    if (marginPct === null) return "text-muted";
    if (marginPct < 30) return "text-danger font-weight-bold";
    if (marginPct < 50) return "text-warning font-weight-bold";
    return "text-success";
  };

  const loading = loadingProf || loadingFC;

  return (
    <>
      <HeaderTitle title={lang("Costo de Alimentos", "Food Cost")} />
      <InventoryNav active="food-cost" />

      {/* Filters + links to sub-reports */}
      <div className="row mb-3">
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Desde", "From")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Hasta", "To")}
          </label>
          <input
            type="date"
            className="form-control form-control-sm"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="col-lg-2">
          <label className="small text-muted mb-1">
            {lang("Sucursal", "Branch")}
          </label>
          <select
            className="custom-select custom-select-sm"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
          >
            <option value="">{lang("Todas", "All")}</option>
            {branchData?.getBranches?.map((b: any) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-4 d-flex align-items-end">
          <button
            className="btn btn-outline-primary btn-sm mr-2"
            onClick={() =>
              history.push(
                `/inventory/cost-drivers?startDate=${startDate}&endDate=${endDate}${
                  branchFilter ? `&branch_id=${branchFilter}` : ""
                }`
              )
            }
          >
            <i className="fa fa-chart-bar mr-1" />
            {lang("Top Costos", "Cost Drivers")}
          </button>
          <button
            className="btn btn-outline-info btn-sm"
            onClick={() =>
              history.push(
                `/inventory/theoretical-vs-actual?startDate=${startDate}&endDate=${endDate}${
                  branchFilter ? `&branch_id=${branchFilter}` : ""
                }`
              )
            }
          >
            <i className="fa fa-balance-scale mr-1" />
            {lang("Teórico vs Real", "Theoretical vs Actual")}
          </button>
        </div>
      </div>

      {loading && <LoaderDots fullSize />}

      {(report || foodCost) && (
        <>
          {/* Summary cards */}
          <div className="row mb-3">
            <div className="col-lg-3">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("% Costo Alimento", "Food Cost %")}
                </small>
                <h3
                  className={
                    foodCost?.food_cost_pct > 35
                      ? "text-danger"
                      : foodCost?.food_cost_pct > 25
                      ? "text-warning"
                      : "text-success"
                  }
                >
                  {foodCost ? `${foodCost.food_cost_pct.toFixed(1)}%` : "—"}
                </h3>
                {foodCost && (
                  <small className="text-muted">
                    ${Number(foodCost.total_ingredient_cost).toLocaleString(
                      "es-MX",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 }
                    )}{" "}
                    / $
                    {Number(foodCost.total_revenue).toLocaleString("es-MX", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </small>
                )}
              </div>
            </div>
            <div className="col-lg-3">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Productos", "Products")}
                </small>
                <h3>{report?.total_items || 0}</h3>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Con receta", "With recipe")}
                </small>
                <h3>{report?.items_with_recipe || 0}</h3>
              </div>
            </div>
            <div className="col-lg-3">
              <div className="material_card p-3 text-center">
                <small className="text-muted">
                  {lang("Margen promedio", "Avg margin")}
                </small>
                <h3 className={getMarginClass(report?.avg_margin_pct || null)}>
                  {report?.avg_margin_pct != null
                    ? `${report.avg_margin_pct.toFixed(1)}%`
                    : "—"}
                </h3>
              </div>
            </div>
          </div>

          {/* Profitability table */}
          <div className="row">
            <div className="col-lg-12">
              <div className="material_card p-3 table-responsive">
                <table className="table table-hover vertical_middle">
                  <thead className="thead-light">
                    <tr>
                      <th>{lang("Producto", "Product")}</th>
                      <th className="text-right">
                        {lang("Precio", "Price")}
                      </th>
                      <th className="text-right">
                        {lang("Costo", "Cost")}
                      </th>
                      <th className="text-right">
                        {lang("Margen $", "Margin $")}
                      </th>
                      <th className="text-right">
                        {lang("Margen %", "Margin %")}
                      </th>
                      <th className="text-center">
                        {lang("Receta", "Recipe")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.product_id}>
                        <td className="font-weight-bold">
                          {item.product?.title || `#${item.product_id}`}
                        </td>
                        <td className="text-right">
                          ${item.price.toFixed(2)}
                        </td>
                        <td className="text-right">
                          {item.food_cost != null
                            ? `$${item.food_cost.toFixed(2)}`
                            : "—"}
                        </td>
                        <td className="text-right">
                          {item.margin != null
                            ? `$${item.margin.toFixed(2)}`
                            : "—"}
                        </td>
                        <td
                          className={`text-right ${getMarginClass(
                            item.margin_pct
                          )}`}
                        >
                          {item.margin_pct != null
                            ? `${item.margin_pct.toFixed(1)}%`
                            : "—"}
                        </td>
                        <td className="text-center">
                          {item.has_recipe ? (
                            <span className="badge badge-success">
                              {lang("Sí", "Yes")}
                            </span>
                          ) : (
                            <span className="badge badge-secondary">No</span>
                          )}
                        </td>
                      </tr>
                    ))}
                    {items.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4">
                          {lang("No hay productos", "No products")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default FoodCostList;
